import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { CoreTargetService } from '../core/services/target.service';
import { TimezoneService } from '../services/timezone.service';
import { LocalStorageService } from '../core/services/local-storage.service';

/**
 * Applies date shifting for behamicsSample target (except /recorder/)
 */
export const ShiftDatesInterceptor: HttpInterceptorFn = (req, next) => {
  const localStorageService = inject(LocalStorageService);

  const target = inject(CoreTargetService).getSelectedTarget('target');
  const path = req.url.split('?')[0] || '';
  if (target !== 'behamicsSample' || path.includes('/recorder/')) {
    return next(req);
  }

  const timeZone = inject(TimezoneService).getTimezone();

  const shift = (urlString: string, reqBody: any): string => {
    const isAbsolute = /^[a-z][a-z0-9+.+-]*:\/\//i.test(urlString);
    const urlObj = isAbsolute
      ? new URL(urlString)
      : new URL(urlString, window.location.origin);
    const params = new URLSearchParams(urlObj.search);

    // Helpers
    const msPerDay = 24 * 60 * 60 * 1000;

    const ymdInZone = (base: Date) => {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(base);
      const get = (t: string) => parts.find((p) => p.type === t)?.value!;
      return { y: get('year'), m: get('month'), d: get('day') };
    };

    const startOfDayUtcInZone = (base: Date) => {
      const { y, m, d } = ymdInZone(base);
      const local = `${y}-${m}-${d}T00:00:00.000`;
      return zonedTimeToUtc(local, timeZone);
    };

    const endOfDayUtcInZone = (base: Date) => {
      const { y, m, d } = ymdInZone(base);
      const local = `${y}-${m}-${d}T23:59:59.000`;
      return zonedTimeToUtc(local, timeZone);
    };

    const atWallClockUtc = (base: Date, h: number, mi = 0, s = 0, ms = 0) => {
      const { y, m, d } = ymdInZone(base);
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const pad3 = (n: number) => String(n).padStart(3, '0');
      const local = `${y}-${m}-${d}T${pad2(h)}:${pad2(mi)}:${pad2(s)}.${pad3(
        ms
      )}`;
      return zonedTimeToUtc(local, timeZone);
    };

    const sameZonedDay = (a: Date, b: Date) => {
      const A = ymdInZone(a);
      const B = ymdInZone(b);
      return A.y === B.y && A.m === B.m && A.d === B.d;
    };

    // Existing constraint anchor
    const maxDate = new Date(
      new Date('2025-02-12').setHours(new Date().getHours(), 59, 59)
    );

    // Read previously selected dates (decode if URL-encoded)
    const rawStart: string = localStorageService.get('startDate') ?? '';
    const [startIso] = decodeURIComponent(rawStart).split('@');
    const userSelectedStartDate = new Date(startIso || 0);

    const rawEnd: string = localStorageService.get('endDate') ?? '';
    const [endIso] = decodeURIComponent(rawEnd).split('@');
    const userSelectedEndDate = new Date(endIso || 0);

    // Guard
    if (
      userSelectedStartDate.getFullYear() <= 2000 ||
      userSelectedEndDate.getFullYear() <= 2000
    ) {
      return urlString;
    }

    // --- Timezone-aware anchors ---
    const zonedNow = utcToZonedTime(new Date(), timeZone);
    const zonedMax = utcToZonedTime(maxDate, timeZone);

    // --- Compute day span between user-selected start and end (in TZ, using day boundaries) ---
    const userStartDayUtc = startOfDayUtcInZone(userSelectedStartDate);
    const userEndDayUtc = startOfDayUtcInZone(userSelectedEndDate);
    const dateSpanDays = Math.floor(
      (userEndDayUtc.getTime() - userStartDayUtc.getTime()) / msPerDay
    );

    // --- Compute how many days user end-day is from "today" (in TZ) ---
    const todayDayUtc = startOfDayUtcInZone(zonedNow);
    const endDateDiffDays = Math.floor(
      (todayDayUtc.getTime() - userEndDayUtc.getTime()) / msPerDay
    );

    // --- Anchor to maxDate: map the user's end-day to a day relative to maxDate ---
    let storedEndDate = new Date(maxDate);
    if (endDateDiffDays > 0) {
      storedEndDate.setDate(storedEndDate.getDate() - endDateDiffDays);
    }

    // --- Stored start = storedEnd - span ---
    const storedStartDate = new Date(storedEndDate);
    storedStartDate.setDate(storedStartDate.getDate() - dateSpanDays);

    const isUserEndToday = sameZonedDay(userSelectedEndDate, zonedNow);

    // If the user's END day is "today" in the target timezone, extend the window by one more day on the start.
    const effectiveStartDate = new Date(storedStartDate);
    if (isUserEndToday) {
      effectiveStartDate.setDate(effectiveStartDate.getDate() - 1);
    }

    // --- Construct UTC instants per rules ---
    // startDate = start-of-day in TZ (00:00:00.000)
    const startUtc = startOfDayUtcInZone(effectiveStartDate);

    // endDate:
    // - if user's end-day is "today" in TZ, then use the **time of maxDate** in TZ
    //   (i.e., we mirror "current time" but capped by maxDate's clock)
    // - otherwise 23:59:59 of that mapped end-day
    const endUtc = isUserEndToday
      ? atWallClockUtc(
          storedEndDate,
          zonedMax.getHours(),
          zonedMax.getMinutes(),
          zonedMax.getSeconds(),
          0
        )
      : endOfDayUtcInZone(storedEndDate);

    // Apply to body if keys are present (ISO@TZ)
    if (reqBody) {
      if (reqBody['startDate'] !== undefined) {
        reqBody['startDate'] = `${startUtc.toISOString()}@${timeZone}`;
      }
      if (reqBody['endDate'] !== undefined) {
        reqBody['endDate'] = `${endUtc.toISOString()}@${timeZone}`;
      }
    }

    // Update ONLY startDate/endDate in URL params
    params.set('startDate', `${startUtc.toISOString()}@${timeZone}`);
    params.set('endDate', `${endUtc.toISOString()}@${timeZone}`);

    // Rebuild the search string to keep it readable (avoid %40, %3A where possible)
    const updatedParams: string[] = [];
    params.forEach((value, key) => {
      updatedParams.push(`${key}=${decodeURIComponent(value)}`);
    });
    urlObj.search = '?' + updatedParams.join('&');
    return isAbsolute
      ? `${urlObj.origin}${urlObj.pathname}${urlObj.search}`
      : `${urlObj.pathname}${urlObj.search}`;
  };

  const shiftedUrl = shift(req.url, req.body);
  if (shiftedUrl === req.url) return next(req);
  return next(req.clone({ url: shiftedUrl, body: req.body }));
};
